import { Input, Modal, type ModalProps } from "@snapchat/snap-design-system";
import { StatusType } from "@snapchat/snap-design-system/lib/components/Control/types";
import { type FC, useCallback, useState } from "react";
import { useOnboardingOrganization } from "../hooks/useOnboardingOrganizations";

interface NewOrganizationModalProps extends Omit<ModalProps, "children"> {
    handleNewOrganization: (organization: string) => void;
}

// Validation rules set via snap-canvas-web/snap-connect-developer-portal
const reservedNameExclusions = /^(?!.*snapchat|.*snap inc|.*bitmoji).*/i;
const minLength = 3;
const maxLength = 100;

const schema = [
    {
        test: (value: string) => value.length < minLength || value.length > maxLength,
        error: `Organization Name is too short (must be between ${minLength} and ${maxLength} characters)`,
    },
    {
        test: (value: string) => !reservedNameExclusions.test(value),
        error: "Organization Name is not formatted correctly",
    },
];

export const NewOrganizationModal: FC<NewOrganizationModalProps> = ({ visible, handleNewOrganization, onCancel }) => {
    const [value, setValue] = useState("");
    const [error, setError] = useState("");
    const { createOrg, isCreating } = useOnboardingOrganization();

    const [statusType, setStatusType] = useState<StatusType | undefined>(undefined);

    const handleInput = useCallback((value: string) => {
        setError("");
        setStatusType(undefined);
        setValue(value);
    }, []);

    const validate = useCallback((value: string) => {
        const error = schema.find((validation) => validation.test(value))?.error ?? "";

        if (error) {
            setError(error);
            setStatusType(StatusType.ERROR);
        }

        return error;
    }, []);

    const onSubmit = useCallback(async () => {
        const error = validate(value);

        if (!error) {
            setStatusType(StatusType.SUCCESS);

            try {
                const { data, errors } = await createOrg({
                    variables: {
                        orgName: value,
                    },
                });
                if (errors) {
                    setStatusType(StatusType.ERROR);
                    setError("There was an error creating your organization.");
                } else {
                    setStatusType(undefined);
                    setValue("");
                    const newOrgId = data?.createOrganization?.organization?.id;

                    if (newOrgId) {
                        handleNewOrganization(newOrgId);
                    }
                }
            } catch (e) {
                setStatusType(StatusType.ERROR);
                setError("There was an error processing your request. Please try again");
            }
        }
    }, [validate, value, createOrg, handleNewOrganization]);

    const handleCancel = () => {
        setStatusType(undefined);
        setValue("");
        setError("");
        onCancel?.();
    };

    return (
        <Modal
            title="New Organization"
            visible={visible}
            onAction={onSubmit}
            actionText="Create"
            isActionButtonPending={isCreating}
            onCancel={handleCancel}
            cancelText="Cancel"
            isCancelButtonDisabled={isCreating}
            data-testid="create-organization-modal"
        >
            <Input
                label="Organization Name"
                value={value}
                onChange={handleInput}
                status={error}
                statusType={statusType}
                disabled={isCreating}
                autoFocus
                data-testid="organization-name-input"
            ></Input>
        </Modal>
    );
};
